<template>
  <b-modal
    id="product-checkout"
    :visible="visible"
    :title="product ? product.name : 'Purchase'"
    centered
    no-close-on-backdrop
    @show="onShow"
    @hidden="onHidden"
  >
    <b-overlay
      :show="isSubmitting || isModalLoading"
      variant="white"
      opacity="1"
      rounded="sm"
    >
      <!-- ── Step 1 : configuration ──────────────────────────── -->
      <b-row v-if="!showPaymentDetails && product" class="p-2">

        <b-col
          cols="12"
          class="mb-3"
        >
          <div v-html="product.description" />
          <hr>
        </b-col>

        <b-col
          cols="12"
          class="mb-3"
        >
          <b-form-group
            label="Application selection"
          >
            <v-select
              v-model="selectedApplication"
              :options="applicationsList"
              label="user_full_name"
              class="customSelect"
              placeholder="Please Select Application"
              :clearable="false"
              @input="selectApp"
            >
              <template
                #selected-option="{ program_name, user_full_name }"
              >
                <span>{{ user_full_name }}</span>&nbsp;-&nbsp;<span>{{ program_name }}</span>
              </template>

              <template
                #option="{ program_name, user_full_name }"
              >
                <span>{{ user_full_name }}</span> - <span>{{ program_name }}</span>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Options section — mirrors "Charges Information" in MakePayment -->
        <b-col v-if="hasOptions" cols="12" class="mb-3">
          <div class="d-flex justify-content-between bg-light py-1 px-3 mb-2 rounded-lg">
            <span>Product Options</span><span>Pay amount</span>
          </div>

          <!-- Radio (single-select) -->
          <div v-if="product.selection_type === 'radio'">
            <div
              v-for="opt in product.options"
              :key="opt.id"
              class="d-flex align-items-start mb-1 w-100 charge px-2"
            >
              <b-form-radio
                v-model="selectedRadioOption"
                :value="opt"
                class="mr-1 mt-1"
                name="product-option"
              />
              <div class="flex-grow-1">
                <span>{{ opt.label }}</span>
                <small v-if="opt.description" class="d-block text-muted">{{ opt.description }}</small>

                <small
                  v-if="isOptionSelected(opt) && opt.price_type === 'custom' && !isOptionAmountInvalid(opt)"
                  class="text-primary d-block mt-1"
                >{{ optionAmountInfoMessage(opt) }}</small>
              </div>

              <!-- Compact quantity selector — revealed only once the option is selected -->
              <div
                v-if="opt.quantity_enabled && isOptionSelected(opt)"
                class="option-qty-stepper mr-2"
              >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="optionQuantities[opt.id] <= 1"
                  @click="decrementOptionQuantity(opt)"
                >−</button>
                <input
                  v-model.number="optionQuantities[opt.id]"
                  type="number"
                  class="option-qty-stepper__input"
                  min="1"
                  :max="opt.max_quantity != null ? opt.max_quantity : undefined"
                  @change="clampOptionQuantity(opt)"
                >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="opt.max_quantity != null && optionQuantities[opt.id] >= opt.max_quantity"
                  @click="incrementOptionQuantity(opt)"
                >+</button>
              </div>

              <div class="charge-amount-input ml-2">
                <b-input-group prepend="$" size="sm">
                  <b-form-input
                    v-model.number="optionAmounts[opt.id]"
                    type="number"
                    size="sm"
                    :min="opt.min_amount != null ? opt.min_amount : 0"
                    :max="opt.max_amount != null ? opt.max_amount : undefined"
                    :disabled="opt.price_type === 'fixed' || !isOptionSelected(opt)"
                    :class="{ 'is-invalid': isOptionAmountInvalid(opt) }"
                    style="width: 90px;"
                  />
                </b-input-group>
                <small
                  v-if="isOptionSelected(opt) && isOptionAmountInvalid(opt)"
                  class="text-danger d-block text-right"
                >{{ optionAmountValidationMessage(opt) }}</small>
                <small
                  v-if="opt.quantity_enabled && isOptionSelected(opt)"
                  class="text-muted d-block text-right mt-25"
                >{{ optionLineTotal(opt) | fixAmount }}</small>
              </div>
            </div>
          </div>

          <!-- Checkbox (multi-select) -->
          <div v-else>
            <div
              v-for="opt in product.options"
              :key="opt.id"
              class="d-flex align-items-start mb-1 w-100 charge px-2"
            >
              <b-form-checkbox
                v-model="selectedCheckboxOptions"
                :value="opt"
                class="mr-1 mt-1"
              />
              <div class="flex-grow-1">
                <span>{{ opt.label }}</span>
                <small v-if="opt.description" class="d-block text-muted">{{ opt.description }}</small>

                <small
                  v-if="isOptionSelected(opt) && opt.price_type === 'custom' && !isOptionAmountInvalid(opt)"
                  class="text-primary d-block mt-1"
                >{{ optionAmountInfoMessage(opt) }}</small>
              </div>

              <!-- Compact quantity selector — revealed only once the option is selected -->
              <div
                v-if="opt.quantity_enabled && isOptionSelected(opt)"
                class="option-qty-stepper mr-2"
              >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="optionQuantities[opt.id] <= 1"
                  @click="decrementOptionQuantity(opt)"
                >−</button>
                <input
                  v-model.number="optionQuantities[opt.id]"
                  type="number"
                  class="option-qty-stepper__input"
                  min="1"
                  :max="opt.max_quantity != null ? opt.max_quantity : undefined"
                  @change="clampOptionQuantity(opt)"
                >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="opt.max_quantity != null && optionQuantities[opt.id] >= opt.max_quantity"
                  @click="incrementOptionQuantity(opt)"
                >+</button>
              </div>

              <div class="charge-amount-input ml-2">
                <b-input-group prepend="$" size="sm">
                  <b-form-input
                    v-model.number="optionAmounts[opt.id]"
                    type="number"
                    size="sm"
                    :min="opt.min_amount != null ? opt.min_amount : 0"
                    :max="opt.max_amount != null ? opt.max_amount : undefined"
                    :disabled="opt.price_type === 'fixed' || !isOptionSelected(opt)"
                    :class="{ 'is-invalid': isOptionAmountInvalid(opt) }"
                    style="width: 90px;"
                  />
                </b-input-group>
                <small
                  v-if="isOptionSelected(opt) && isOptionAmountInvalid(opt)"
                  class="text-danger d-block text-right"
                >{{ optionAmountValidationMessage(opt) }}</small>
                <small
                  v-if="opt.quantity_enabled && isOptionSelected(opt)"
                  class="text-muted d-block text-right mt-25"
                >{{ optionLineTotal(opt) | fixAmount }}</small>
              </div>
            </div>
          </div>
          <hr>
        </b-col>

        <!-- Custom amount -->
        <b-col v-if="product.allow_custom_amount && !hasOptions" cols="12" class="mb-3">
          <b-form-group
            :label="'Amount ($)'"
          >
            <div class="d-flex align-items-center">
              <b-input-group prepend="$" class="flex-grow-1">
                <b-form-input
                  v-model.number="customAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </b-input-group>

              <!-- Compact quantity selector for simple products with quantity enabled -->
              <div
                v-if="product.quantity_enabled"
                class="option-qty-stepper ml-2"
              >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="productQuantity <= 1"
                  @click="decrementProductQuantity"
                >−</button>
                <input
                  v-model.number="productQuantity"
                  type="number"
                  class="option-qty-stepper__input"
                  min="1"
                  :max="product.max_quantity != null ? product.max_quantity : undefined"
                  @change="clampProductQuantity"
                >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="product.max_quantity != null && productQuantity >= product.max_quantity"
                  @click="incrementProductQuantity"
                >+</button>
              </div>
            </div>
          </b-form-group>
        </b-col>

        <!-- Subtotal (read-only) -->
        <b-col
          v-else
          cols="12"
        >
          <div class="d-flex justify-content-between align-items-center mb-4">
            <span class="form__row-label mb-0">Subtotal</span>
            <div class="d-flex align-items-center px-2">
              <!-- Compact quantity selector for simple fixed-price products with quantity enabled -->
              <div
                v-if="product.quantity_enabled && !hasOptions"
                class="option-qty-stepper mr-2"
              >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="productQuantity <= 1"
                  @click="decrementProductQuantity"
                >−</button>
                <input
                  v-model.number="productQuantity"
                  type="number"
                  class="option-qty-stepper__input"
                  min="1"
                  :max="product.max_quantity != null ? product.max_quantity : undefined"
                  @change="clampProductQuantity"
                >
                <button
                  type="button"
                  class="option-qty-stepper__btn"
                  :disabled="product.max_quantity != null && productQuantity >= product.max_quantity"
                  @click="incrementProductQuantity"
                >+</button>
              </div>
              <span class="font-weight-bold">${{ totalAmount.toFixed(2) }}</span>
            </div>
          </div>
          <hr>
        </b-col>

        <!-- Applicable fees preview -->
        <b-col
          v-if="paymentMethod && (paymentMethod !== 'Credit Card' || selectedCard) && applicableFees.length > 0"
          cols="12"
          class="mb-3"
        >
          <span class="form__row-label">Fees $</span>
          <div
            v-for="fee in applicableFees"
            :key="fee.id"
            class="d-flex justify-content-between mb-1 px-2"
          >
            <span class="text-muted">{{ fee.name }}</span>
            <span class="text-muted">{{ fee.calculatedAmount | fixAmount }}</span>
          </div>
        </b-col>

        <!-- Payment method -->
        <b-col cols="12" class="mb-3">
          <b-form-group label="Select Payment Method">
            <v-select
              v-model="paymentMethod"
              :options="effectivePaymentMethodsList"
              class="customSelect"
              placeholder="Please Select Payment Method"
              :clearable="false"
              :searchable="false"
            >
              <template #selected-option="{ label }">
                <div
                  v-if="label === 'Cash Bank'"
                  class="d-flex justify-content-between w-100"
                >
                  <span>{{ label }}</span>
                  <span class="text-muted ml-2">({{ cashBankBalance | fixAmount }})</span>
                </div>
                <div v-else>{{ label }}</div>
              </template>
              <template #option="{ label }">
                <div
                  v-if="label === 'Cash Bank'"
                  class="d-flex justify-content-between"
                >
                  <span>{{ label }}</span>
                  <span>{{ cashBankBalance | fixAmount }}</span>
                </div>
                <div v-else>{{ label }}</div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Cash Bank: insufficient balance warning -->
        <b-col v-if="cashBankInsufficientFunds" cols="12" class="mb-3">
          <p class="text-danger mb-0">
            Insufficient Cash Bank balance. Available:
            <strong>{{ cashBankBalance | fixAmount }}</strong>,
            required: <strong>{{ totalAmount | fixAmount }}</strong>.
          </p>
        </b-col>

        <!-- Credit card list -->
        <b-col v-if="isCC" cols="12" class="mb-1">
          <b-form-group label="Select Credit Card">
            <CreditCardRadioBlock
              v-for="card in preparedCardsList"
              :key="card.id"
              :card="card"
              :cards-list="preparedCardsList"
            />
            <b-button
              variant="primary"
              class="w-100 mt-2"
              @click="$bvModal.show('v-b-modal.add-new-card')"
            >
              Add New Credit Card +
            </b-button>
          </b-form-group>
        </b-col>

        <!-- ACH / Bank account list -->
        <b-col v-if="isACH" cols="12" class="mb-1">
          <b-form-group label="Select Bank Account">
            <CreditCardRadioBlock
              v-for="card in preparedCardsList"
              :key="card.id"
              :card="card"
              :cards-list="preparedCardsList"
            />
            <b-button
              variant="primary"
              class="w-100 mt-2"
              @click="$bvModal.show('add-new-bank-account')"
            >
              Add Bank Account +
            </b-button>
          </b-form-group>
        </b-col>

        <!-- Note (optional) -->
        <b-col cols="12" class="mb-3">
          <b-form-group label="Additional Notes (Optional)">
            <b-form-textarea
              v-model="notes"
              placeholder="Add any additional information or notes here…"
              rows="3"
              max-rows="6"
              no-resize
            />
          </b-form-group>
        </b-col>

        <!-- Inline error -->
        <b-col v-if="errorMessage" cols="12" class="mb-1">
          <p class="text-danger text-center">{{ errorMessage }}</p>
        </b-col>
      </b-row>

      <!-- ── Step 2 : payment summary ───────────────────────── -->
      <ProductCheckoutPaymentInfo
        v-if="showPaymentDetails && product"
        :product="product"
        :selected-options="selectedOptionsWithAmounts"
        :custom-amount="customAmount"
        :has-options="hasOptions"
        :quantity="hasOptions ? 1 : productQuantity"
        :subtotal="totalAmount"
        :payment-method="paymentMethod"
        :card="selectedCardInfo"
        :payment-fees="paymentFees"
      />
    </b-overlay>

    <!-- ── Footer ─────────────────────────────────────────── -->
    <template #modal-footer="{ cancel }">
      <!-- Step 1 footer -->
      <div v-if="!showPaymentDetails" class="w-100 d-flex justify-content-between">
        <b-button
          class="border border-primary"
          variant="outline-primary"
          :disabled="isSubmitting"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="isSubmitting || !canProceedToReview"
          @click="showPaymentDetails = true"
        >
          Review &amp; Pay
        </b-button>
      </div>

      <!-- Step 2 footer -->
      <div v-else class="w-100 d-flex justify-content-between">
        <b-button
          class="border border-primary"
          variant="outline-primary"
          :disabled="isSubmitting"
          @click="showPaymentDetails = false"
        >
          Back
        </b-button>
        <b-button
          variant="primary"
          :disabled="isSubmitting"
          @click="submit"
        >
          <b-spinner v-if="isSubmitting" small class="mr-1" />
          Confirm Purchase
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BOverlay, BRow, BCol, BFormGroup,
  BButton, BSpinner, BFormRadio, BFormCheckbox,
  BFormInput, BInputGroup, BFormTextarea,
  VBModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

import { filters } from '@core/mixins/filters'
import { setStatusModalData } from '@core/mixins/setStatusModalData'
import { calculateApplicableFees } from '@/utils/payment'

import CreditCardRadioBlock from '@/components/site/CreditCardRadioBlock.vue'
import ProductCheckoutPaymentInfo from './ProductCheckoutPaymentInfo.vue'
import { mapGetters } from 'vuex'
import { getUserData } from '@/auth/utils'

export default {
  name: 'ProductCheckout',
  components: {
    BModal,
    BOverlay,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BSpinner,
    BFormRadio,
    BFormCheckbox,
    BFormInput,
    BInputGroup,
    BFormTextarea,
    vSelect,
    CreditCardRadioBlock,
    ProductCheckoutPaymentInfo,
  },
  mixins: [filters, setStatusModalData],
  directives: {
    'b-modal': VBModal,
  },

  data() {
    return {
      userId: getUserData().id,
      showPaymentDetails: false,
      selectedRadioOption: null,
      selectedCheckboxOptions: [],
      customAmount: null,
      errorMessage: '',
      /** Tracks the live amount for every option, keyed by option id */
      optionAmounts: {},
      /** Tracks the live quantity for every quantity-enabled option, keyed by option id */
      optionQuantities: {},
      /** Tracks the live quantity for a simple (no-options) quantity-enabled product */
      productQuantity: 1,
      notes: '',
    }
  },

  computed: {
    ...mapGetters({
      applicationsList: 'paymentPortal/getApplications',
      paymentMethodsList: 'paymentPortal/getPaymentMethodsList',
      cardsList: 'cards/getCardsList',
      selectedCardInfo: 'cards/getSelectedCardInfo',
      paymentFees: 'paymentPortal/getPaymentFees',
      cashBankBalance: 'paymentPortal/getCashBankBalance',
    }),
    selectedApplication: {
      get: function () {
        return this.$store.getters['paymentPortal/getSelectedApplication']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_SLECTED_APPLICATION', val)
      }
    },
    visible() {
      return this.$store.getters['productCatalog/getCheckoutVisible']
    },
    product() {
      return this.$store.getters['productCatalog/getCheckoutProduct']
    },
    isSubmitting() {
      return this.$store.getters['productCatalog/getIsSubmitting']
    },
    paymentMethod: {
      get: function () {
        return this.$store.getters['paymentPortal/getPaymentMethod']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_PAYMENT_METHOD', val)
      }
    },
    selectedCard: {
      get: function () {
        return this.$store.getters['cards/getSelectedCreditCard']
      },
      set: function (val) {
        this.$store.commit('cards/SET_SELECTED_CREDIT_CARD', val)
      }
    },
    isModalLoading: {
      get: function () {
        return this.$store.getters['paymentPortal/getIsModalLoaidng']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_MODAL_LOADING_STATE', val)
      },
    },
    /** Payment methods visible to the user for this product.
     *  If the product defines portal_payment_methods (non-empty), only those
     *  are shown; otherwise fall back to the full list from the store. */
    effectivePaymentMethodsList() {
      const allowed = this.product?.portal_payment_methods
      if (allowed && allowed.length > 0) {
        return this.paymentMethodsList.filter(m => allowed.includes(m))
      }
      return this.paymentMethodsList
    },

    isCC() {
      return this.paymentMethod === 'Credit Card'
    },
    isACH() {
      return this.paymentMethod === 'ACH'
    },
    isCashBank() {
      return this.paymentMethod === 'Cash Bank'
    },
    cashBankInsufficientFunds() {
      return this.isCashBank && this.totalAmount > 0 && this.cashBankBalance < this.totalAmount
    },
    preparedCardsList() {
      if (this.isCC) {
        return this.cardsList.filter(card => card.type.toLowerCase() !== 'ach')
      }
      if (this.isACH) {
        return this.cardsList.filter(card => card.type.toLowerCase() === 'ach')
      }
      return []
    },

    // ── Options helpers ─────────────────────────────────────
    hasOptions() {
      return !!(this.product?.options?.length)
    },
    /**
     * Unified array of currently selected options.
     * Radio → always 0 or 1 item; Checkbox → 0..N items.
     */
    selectedOptions() {
      if (!this.product) return []
      if (this.product.selection_type === 'radio') {
        return this.selectedRadioOption ? [this.selectedRadioOption] : []
      }
      return this.selectedCheckboxOptions
    },

    /**
     * Line total for a single option.
     * Fixed  → unit amount × quantity.
     * Custom → the entered amount is already treated as the full line total.
     */
    optionLineTotal() {
      return opt => {
        const amount = Number(this.optionAmounts[opt.id]) || 0
        if (opt.price_type === 'fixed' && opt.quantity_enabled) {
          const qty = Number(this.optionQuantities[opt.id]) || 1
          return Math.round(amount * qty * 100) / 100
        }
        return amount
      }
    },

    optionsTotal() {
      // Use the live optionAmounts/optionQuantities values, not the static opt.amount from the product config
      return this.selectedOptions.reduce((sum, o) => sum + this.optionLineTotal(o), 0)
    },

    /**
     * Line total for a simple product (no options).
     * Custom amount → the entered amount is treated as the full line total (quantity informational).
     * Fixed amount  → base price × quantity (quantity defaults to 1 when quantity is not enabled).
     */
    simpleProductTotal() {
      if (!this.product || this.hasOptions) return 0
      if (this.product.allow_custom_amount) {
        return Number(this.customAmount) || 0
      }
      const base = Number(this.product.price?.amount) || 0
      const qty = this.product.quantity_enabled ? (Number(this.productQuantity) || 1) : 1
      return Math.round(base * qty * 100) / 100
    },

    /** Sub-total before any payment fee */
    totalAmount() {
      if (this.hasOptions) {
        return Math.round((this.optionsTotal + (Number(this.customAmount) || 0)) * 100) / 100
      }
      return Math.round(this.simpleProductTotal * 100) / 100
    },

    // ── Fee calculation ──────────────────────────────────────
    effectivePaymentMethod() {
      if (this.isCC && this.selectedCardInfo) {
        return this.selectedCardInfo.type?.toLowerCase() === 'ach' ? 'ACH' : 'Credit Card'
      }
      return this.paymentMethod
    },

    applicableFees() {
      return calculateApplicableFees(this.paymentFees, this.effectivePaymentMethod, this.totalAmount)
    },

    totalFeeAmount() {
      return this.applicableFees.reduce((sum, f) => sum + f.calculatedAmount, 0)
    },

    /** Final amount charged to the user (sub-total + fees) */
    grandTotal() {
      return Math.round((this.totalAmount + this.totalFeeAmount) * 100) / 100
    },

    /**
     * Validates all selected options' amounts.
     * Returns a human-readable error string or null.
     */
    optionsAmountError() {
      for (const opt of this.selectedOptions) {
        const amount = Number(this.optionAmounts[opt.id] || 0)
        if (opt.price_type === 'custom') {
          if (amount <= 0) return `Please enter a valid amount for "${opt.label}".`
          if (opt.min_amount != null && amount < Number(opt.min_amount)) {
            return `Minimum amount for "${opt.label}" is $${Number(opt.min_amount).toFixed(2)}.`
          }
          if (opt.max_amount != null && amount > Number(opt.max_amount)) {
            return `Amount for "${opt.label}" cannot exceed $${Number(opt.max_amount).toFixed(2)}.`
          }
        }
      }
      return null
    },

    /**
     * Validates the quantity selected for each option (when quantity is enabled).
     * Returns a human-readable error string or null.
     */
    optionsQuantityError() {
      for (const opt of this.selectedOptions) {
        if (!opt.quantity_enabled) continue
        const qty = Number(this.optionQuantities[opt.id])
        if (!Number.isInteger(qty) || qty < 1) {
          return `Please select a valid quantity for "${opt.label}".`
        }
        if (opt.max_quantity != null && qty > Number(opt.max_quantity)) {
          return `Quantity for "${opt.label}" cannot exceed ${opt.max_quantity}.`
        }
      }
      return null
    },

    /**
     * Validates the quantity selected for a simple (no-options) quantity-enabled product.
     * Returns a human-readable error string or null.
     */
    productQuantityError() {
      if (this.hasOptions || !this.product?.quantity_enabled) return null
      const qty = Number(this.productQuantity)
      if (!Number.isInteger(qty) || qty < 1) {
        return 'Please select a valid quantity.'
      }
      if (this.product.max_quantity != null && qty > Number(this.product.max_quantity)) {
        return `Quantity cannot exceed ${this.product.max_quantity}.`
      }
      return null
    },

    /**
     * Selected options enriched with the actual amounts/quantities the user has set.
     * This is what gets sent to the backend.
     */
    selectedOptionsWithAmounts() {
      return this.selectedOptions.map(o => ({
        ...o,
        amount: Number(this.optionAmounts[o.id]) || 0,
        quantity: o.quantity_enabled ? (Number(this.optionQuantities[o.id]) || 1) : 1,
        line_total: this.optionLineTotal(o),
      }))
    },

    // ── Validation ───────────────────────────────────────────
    canProceedToReview() {
      if (this.totalAmount <= 0) return false
      if (!this.selectedApplication) return false
      if (!this.paymentMethod) return false
      // For CC / ACH the selected card must exist in the type-filtered list.
      // This prevents an ACH card (set as the application's default) from
      // satisfying a Credit Card–only requirement and vice-versa.
      if ((this.isCC || this.isACH) && !this.preparedCardsList.some(c => c.id === this.selectedCard)) return false
      // Cash Bank: prevent checkout when balance is insufficient
      if (this.cashBankInsufficientFunds) return false
      if (this.hasOptions && this.optionsAmountError) return false
      if (this.hasOptions && this.optionsQuantityError) return false
      return !this.productQuantityError
    },
  },

  watch: {
    /** Pre-select default / first radio option when a product is opened */
    product(product) {
      this.resetSelections()
      if (!product) return
      this.initOptionAmounts(product)
      this.initOptionQuantities(product)
      this.initProductQuantity(product)
      if (product.options?.length && product.selection_type === 'radio') {
        this.selectedRadioOption = product.options.find(o => o.is_default) ?? product.options[0]
      }
      // If the currently selected payment method is not in the effective list, clear it
      if (this.paymentMethod && !this.effectivePaymentMethodsList.includes(this.paymentMethod)) {
        this.paymentMethod = null
      }
    },
  },

  methods: {
    async onShow() {
      this.isModalLoading = true
      await Promise.all([
        this.$store.dispatch('cards/loadCards'),
        this.$store.dispatch('paymentPortal/fetchCashBankBalance'),
        this.$store.dispatch('paymentPortal/fetchApplications', {
          without_archived: false,
          program_id: this.product.program_id,
        }),
      ])
      this.isModalLoading = false
    },
    onHidden() {
      this.reset()
    },
    resetSelections() {
      this.selectedRadioOption = null
      this.selectedCheckboxOptions = []
      this.customAmount = null
      this.selectedApplication = null
      this.selectedCard = null
      this.errorMessage = ''
      this.optionAmounts = {}
      this.optionQuantities = {}
      this.productQuantity = 1
      this.notes = ''
    },
    reset() {
      this.resetSelections()
      this.showPaymentDetails = false
      this.$store.dispatch('productCatalog/closeCheckout')
    },
    selectApp(application) {
      this.isModalLoading = true

      const cardId = application.card_id
      if (cardId && (this.isCC || this.isACH)) {
        // Only accept the application's default card when its type matches
        // the currently selected payment method.
        const card = this.cardsList.find(c => c.id === cardId)
        const isAchCard = card ? card.type.toLowerCase() === 'ach' : false
        const typeMismatch = (this.isCC && isAchCard) || (this.isACH && !isAchCard)
        this.selectedCard = typeMismatch ? null : cardId
      } else {
        this.selectedCard = cardId
      }

      if (application.program_id) {
        this.$store.dispatch('paymentPortal/fetchPaymentFees', application.program_id)
      }
      this.isModalLoading = false
    },
    /**
     * Initialise the optionAmounts map so every option id has a reactive
     * entry before the user touches anything.
     *
     * Fixed options  → locked to the configured amount.
     * Custom options → start at min_amount (if set) so the field isn't
     *                  immediately invalid, otherwise 0.
     */
    initOptionAmounts(product) {
      if (!product?.options?.length) {
        this.optionAmounts = {}
        return
      }
      const amounts = {}
      product.options.forEach(opt => {
        if (opt.price_type === 'fixed') {
          amounts[opt.id] = Number(opt.amount) || 0
        } else {
          // custom: seed with min_amount when provided so the field isn't flagged immediately
          amounts[opt.id] = opt.min_amount != null ? Number(opt.min_amount) : 0
        }
      })
      // Assign as a whole new object so Vue 2 observes all keys
      this.optionAmounts = amounts
    },
    /**
     * Initialise the optionQuantities map so every quantity-enabled option
     * starts at a quantity of 1.
     */
    initOptionQuantities(product) {
      if (!product?.options?.length) {
        this.optionQuantities = {}
        return
      }
      const quantities = {}
      product.options.forEach(opt => {
        if (opt.quantity_enabled) {
          quantities[opt.id] = 1
        }
      })
      // Assign as a whole new object so Vue 2 observes all keys
      this.optionQuantities = quantities
    },
    /** Clamp the quantity value for an option between 1 and its max_quantity (if set) */
    clampOptionQuantity(opt) {
      let qty = Number(this.optionQuantities[opt.id])
      if (!Number.isFinite(qty) || qty < 1) qty = 1
      if (opt.max_quantity != null && qty > Number(opt.max_quantity)) qty = Number(opt.max_quantity)
      this.$set(this.optionQuantities, opt.id, Math.round(qty))
    },
    /** Increase the quantity for an option by 1, capped at max_quantity (if set) */
    incrementOptionQuantity(opt) {
      const current = Number(this.optionQuantities[opt.id]) || 1
      const next = opt.max_quantity != null ? Math.min(current + 1, Number(opt.max_quantity)) : current + 1
      this.$set(this.optionQuantities, opt.id, next)
    },
    /** Decrease the quantity for an option by 1, floored at 1 */
    decrementOptionQuantity(opt) {
      const current = Number(this.optionQuantities[opt.id]) || 1
      this.$set(this.optionQuantities, opt.id, Math.max(current - 1, 1))
    },
    /**
     * Initialise the quantity for a simple (no-options) quantity-enabled product
     * to 1 whenever the product changes.
     */
    initProductQuantity() {
      this.productQuantity = 1
    },
    /** Clamp the simple product's quantity between 1 and its max_quantity (if set) */
    clampProductQuantity() {
      let qty = Number(this.productQuantity)
      if (!Number.isFinite(qty) || qty < 1) qty = 1
      if (this.product?.max_quantity != null && qty > Number(this.product.max_quantity)) {
        qty = Number(this.product.max_quantity)
      }
      this.productQuantity = Math.round(qty)
    },
    /** Increase the simple product's quantity by 1, capped at max_quantity (if set) */
    incrementProductQuantity() {
      const current = Number(this.productQuantity) || 1
      const max = this.product?.max_quantity
      this.productQuantity = max != null ? Math.min(current + 1, Number(max)) : current + 1
    },
    /** Decrease the simple product's quantity by 1, floored at 1 */
    decrementProductQuantity() {
      const current = Number(this.productQuantity) || 1
      this.productQuantity = Math.max(current - 1, 1)
    },
    isOptionSelected(opt) {
      if (this.product?.selection_type === 'radio') {
        return this.selectedRadioOption?.id === opt.id
      }
      return this.selectedCheckboxOptions.some(o => o.id === opt.id)
    },
    /**
     * Returns true when the selected option's entered amount violates
     * the configured constraints.
     */
    isOptionAmountInvalid(opt) {
      if (!this.isOptionSelected(opt)) return false
      const amount = Number(this.optionAmounts[opt.id] || 0)
      if (opt.price_type === 'custom') {
        if (amount <= 0) return true
        if (opt.min_amount != null && amount < Number(opt.min_amount)) return true
        if (opt.max_amount != null && amount > Number(opt.max_amount)) return true
      }
      return false
    },
    /** Human-readable constraint violation message for an option */
    optionAmountValidationMessage(opt) {
      const amount = Number(this.optionAmounts[opt.id] || 0)
      if (amount <= 0) return 'Enter a valid amount'
      if (opt.min_amount != null && amount < Number(opt.min_amount)) {
        return `Min: ${this.$options.filters.fixAmount(Number(opt.min_amount))}`
      }
      if (opt.max_amount != null && amount > Number(opt.max_amount)) {
        return `Max: ${this.$options.filters.fixAmount(Number(opt.max_amount))}`
      }
      return 'Enter a valid amount'
    },
    /** Human-readable constraint violation message for an option */
    optionAmountInfoMessage(opt) {
      const min = opt.min_amount
      const max = opt.max_amount
      const format = (v) => this.$options.filters.fixAmount(Number(v))

      if (min != null && max != null) {
        return `Enter a custom amount between ${format(min)} and ${format(max)}`
      }

      if (min != null) {
        return `Enter a custom amount of at least ${format(min)}`
      }

      if (max != null) {
        return `Enter a custom amount up to ${format(max)}`
      }

      return 'Enter a custom amount'
    },

    // ── Submit ───────────────────────────────────────────────
    async submit() {
      this.errorMessage = ''

      const payload = {
        product_uuid: this.product.uuid,
        quantity: this.hasOptions ? 1 : (this.product.quantity_enabled ? this.productQuantity : 1),
        amount: this.totalAmount,
        type: this.paymentMethod,
        application_id: this.selectedApplication?.id,
        ...(this.isCC && { card_id: this.selectedCard }),
        // Send options with the actual amounts chosen/entered by the user
        ...(this.selectedOptionsWithAmounts.length && { selected_options: this.selectedOptionsWithAmounts }),
        ...(this.notes?.trim() && { notes: this.notes.trim() }),
      }

      try {
        const purchase = await this.$store.dispatch('productCatalog/purchase', payload)

        // The backend syncs the purchase status from the payment gateway synchronously
        // before returning the response.  If the status came back as 'failed' it means
        // the gateway rejected the charge; show a failure modal instead of a success one.
        const paymentFailed = purchase?.status?.toLowerCase() === 'failed'

        if (paymentFailed) {
          this.setStatusModalData({
            title: 'Product Purchase',
            status: 'Failed',
            header: 'Payment failed',
            message: purchase?.failure_reason
              || 'Your payment could not be processed. Please check your payment details and try purchasing the product again.',
          })
        } else if (purchase?.status?.toLowerCase() === 'pending_approval' || purchase?.status?.toLowerCase() === 'pending approval') {
          this.setStatusModalData({
            title: 'Product Purchase',
            status: 'Pending',
            header: 'Purchase submitted for approval',
            message: 'Your purchase request has been submitted and is awaiting admin approval. You will be notified once it is reviewed.',
          })
        } else {
          this.setStatusModalData({
            title: 'Product Purchase',
            status: 'Success',
            header: 'Your purchase has been processed',
            message: '',
          })
        }

        this.$store.dispatch('productCatalog/closeCheckout')
        this.$bvModal.show('status-modal')
      } catch (err) {
        // Immediate failure: the API returned an error (e.g. invalid card, validation
        // error, or gateway exception before the purchase was even created).
        // No purchase record was created in this case.
        const message = err?.response?.data?.message
          || 'Your payment could not be processed. Please check your payment details and try purchasing the product again.'

        this.setStatusModalData({
          title: 'Product Purchase',
          status: 'Failed',
          header: 'Payment failed',
          message,
        })

        // Refresh purchases list — on the rare chance a purchase was soft-deleted
        // or partially created, the list should reflect the current server state.
        this.$store.dispatch('productCatalog/fetchMyPurchases')
        this.$store.dispatch('productCatalog/closeCheckout')
        this.$bvModal.show('status-modal')
      }
    },
  },
}
</script>

<style lang="scss">
/* Reuse MakePayment's charge-row styling for the options section */
#product-checkout {
  .charge {
    .custom-control-label {
      width: 100%;
      padding-right: 20px;
    }
  }

  .charge-amount-input {
    flex-shrink: 0;

    .input-group {
      flex-wrap: nowrap;
    }

    .input-group > .form-control {
      border: 1px solid #d8d6de;
      border-left: 0;

      &.is-invalid {
        border-color: #ea5455;
      }

      &:focus {
        border-color: #7367f0;
        box-shadow: none;
      }

      &:disabled {
        background-color: #efefef;
        border-color: #d8d6de;
      }
    }

    input[type='number'] {
      -moz-appearance: textfield;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  .option-qty-stepper {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
    overflow: hidden;
    height: 29px;

    &__btn {
      width: 24px;
      height: 100%;
      flex-shrink: 0;
      border: none;
      background-color: #f8f8f8;
      color: #6e6b7b;
      font-size: 14px;
      line-height: 1;
      cursor: pointer;
      transition: background-color 0.15s ease, color 0.15s ease;

      &:hover:not(:disabled) {
        background-color: #eeedf0;
        color: #7367f0;
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }

    &__input {
      width: 36px;
      height: 100%;
      border: none;
      border-left: 1px solid #d8d6de;
      border-right: 1px solid #d8d6de;
      text-align: center;
      font-size: 13px;
      padding: 0;
      -moz-appearance: textfield;

      &:focus {
        outline: none;
        box-shadow: none;
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }
}
</style>

